/**
 * Eingaben, Sätze und Regeln des E-Mail-Versands ohne Datenbank (Server, Web-App und Tests mit denselben Regeln).
 */
import { z } from 'zod';
import type { MailPlaceholderGroup } from '@till-erp/platform/mail-placeholders';

import { formatMegabytes, type EmailLanguage as Language } from '@till-erp/collab/values';

export {
  emailLanguageCodes,
  emailLanguages,
  emailOrigins,
  emailOriginText,
  emailStatuses,
  emailStatusText,
  emailWaitReasonText,
  emailTemplateText,
  type ChatterEmailView,
  type EmailLanguage,
  type EmailOrigin,
  type EmailStatus,
} from '@till-erp/collab/values';

/** Gegenstelle und Operation des Versands (Integration `graph-mail`); Kennungen nie umbenennen. */
export const GRAPH_MAIL_COUNTERPART = { id: 'graph-mail', label: 'E-Mail (Microsoft 365)' } as const;
export const SEND_MAIL_OPERATION = 'graph-mail.sendMail';
/** Betriebsmail (Story 2.9): Alarm- und Hinweis-E-Mails; nicht in der Tagesgrenze gezählt oder angehalten. */
export const SEND_URGENT_MAIL_OPERATION = 'graph-mail.sendUrgentMail';
/** Beide Operationen, die eine E-Mail aus `mail.outbound_mail` senden. */
export const sendMailOperations: readonly string[] = [SEND_MAIL_OPERATION, SEND_URGENT_MAIL_OPERATION];

/** Zustände, in denen eine E-Mail fertig ist und „Erneut senden“ angeboten wird. */
export const finishedEmailStatuses = ['sent', 'failed', 'shadowed', 'skipped', 'bounced', 'unsubscribed'] as const;

/** Nutzlast im Ledger: nur der Verweis auf die E-Mail (keine Personendaten, keine Geheimnisse). */
export const sendMailPayload = z.object({ outboundMailId: z.uuid() }).strict();
export type SendMailPayload = z.infer<typeof sendMailPayload>;

export const MAIL_SUBJECT_MAX = 300;
export const MAIL_BODY_MAX = 100_000;

export const MAIL_TEXTS = {
  recipientInvalid: 'Bitte eine gültige E-Mail-Adresse als Empfänger angeben.',
  subjectEmpty: 'Bitte einen Betreff angeben.',
  subjectTooLong: `Der Betreff ist zu lang (höchstens ${MAIL_SUBJECT_MAX} Zeichen).`,
  bodyEmpty: 'Bitte einen Text für die E-Mail angeben.',
  bodyTooLong: 'Der Text der E-Mail ist zu lang.',
  occasionInvalid: 'Der Anlass-Schlüssel ist ungültig (z. B. order_confirmation:<auftrag>).',
  occasionReserved: 'Anlass-Schlüssel mit „manual:“ vergibt nur „Erneut senden“.',
  urgentOnlySystem: 'Betriebsmails stellt nur das System ein, nicht eine Person.',
  resendOfForeign: 'Die frühere E-Mail gehört nicht zu diesem Datensatz.',
  entityUnknown: 'Diese Art von Datensatz kennt das ERP nicht.',
  entityNotFound: 'Diesen Datensatz gibt es nicht.',
  notFound: 'Diese E-Mail gibt es nicht.',
  /** Story 18.5: Betreff und Text nach Löschfrist entfernt (Grabstein in subject und body). */
  contentPurged: 'Inhalt nach Löschfrist entfernt',
  resendPurged: 'Inhalt nach Löschfrist entfernt – diese E-Mail lässt sich nicht mehr erneut senden. Bitte eine neue E-Mail schreiben.',
  resendWhileOpen: 'Diese E-Mail wartet noch auf den Versand. Erneut senden geht erst, wenn sie gesendet oder fehlgeschlagen ist.',
  testForbidden: 'Nur Admins dürfen Test-E-Mails senden.',
  testSubject: 'Test-E-Mail aus dem TILL TOOLS ERP',
  testBody:
    'Diese E-Mail hat das TILL TOOLS ERP zum Test gesendet (Einstellungen › E-Mail). Wenn sie angekommen ist, funktioniert der Versand. Sie können sie löschen.',
  /** Alarm-E-Mail (Story 1.11a): Betreff wie der Klärfall. */
  alarmSubject: (title: string, probe: boolean) => `Alarm: ${title}${probe ? ' (Probe)' : ''}`,
  alarmBody: (detail: string, runbookUrl: string, probe: boolean) =>
    [
      probe ? 'Probealarm – es ist nichts kaputt. So lässt sich prüfen, ob Alarm-E-Mails ankommen.' : 'Im ERP ist ein Alarm aufgetreten.',
      '',
      detail,
      '',
      `Notfall-Handbuch: ${runbookUrl}`,
      'Den Alarm sehen Sie im ERP unter Einstellungen › Betrieb und als Klärfall in den Aufgaben.',
    ].join('\n'),
  /** Hinweis bei Tageskontingent (Story 2.9): Betreff wie der Systemhinweis in der Glocke. */
  quotaWarningBody: (notice: string) =>
    [
      `${notice}.`,
      '',
      'Ist die Grenze erreicht, warten weitere Aufrufe und gehen am Folgetag ab 00:00 in Reihenfolge hinaus; es geht nichts verloren.',
      'Den Stand sehen Sie im ERP unter Einstellungen › E-Mail. Die Grenze ändern Sie unter Einstellungen › Außenwirkungen › Tageskontingente.',
    ].join('\n'),
  /** Hinweis beim KI-Budget (Story 2.21, Retro K9): Betreff wie der Systemhinweis in der Glocke. */
  aiBudgetWarningBody: (notice: string) =>
    [
      `${notice}.`,
      '',
      'Ist das Budget aufgebraucht, pausieren KI-Aufgaben bis zum nächsten Monat; Pflicht-Aufgaben laufen weiter.',
      'Verbrauch und Budget sehen und ändern Sie im ERP unter Einstellungen › KI › Budget.',
    ].join('\n'),
} as const;

export const recipientSchema = z.email({ error: MAIL_TEXTS.recipientInvalid }).max(254, MAIL_TEXTS.recipientInvalid);
export const subjectSchema = z
  .string({ error: MAIL_TEXTS.subjectEmpty })
  .trim()
  .min(1, MAIL_TEXTS.subjectEmpty)
  .max(MAIL_SUBJECT_MAX, MAIL_TEXTS.subjectTooLong);
export const bodySchema = z.string({ error: MAIL_TEXTS.bodyEmpty }).min(1, MAIL_TEXTS.bodyEmpty).max(MAIL_BODY_MAX, MAIL_TEXTS.bodyTooLong);

/**
 * Anlass-Schlüssel: `<anlass>:<bezug>`, z. B. `order_confirmation:0192…`, `test:abnahme-3` oder
 * `datev:<stapel>:<empfänger>` (AD-22, Bezug darf eine E-Mail-Adresse enthalten). Höchstens 300 Zeichen (Datenbank).
 */
export const occasionKeySchema = z
  .string({ error: MAIL_TEXTS.occasionInvalid })
  .max(300, MAIL_TEXTS.occasionInvalid)
  .regex(/^[a-z][a-z0-9_]*:[A-Za-z0-9_.:@+-]+$/, MAIL_TEXTS.occasionInvalid)
  .refine((key) => !key.startsWith('manual:'), MAIL_TEXTS.occasionReserved);

/** Kurzer Anlass einer Test-E-Mail (nur Admin, Schnittstelle), z. B. `abnahme-3`. */
export const testOccasionSchema = z.string({ error: MAIL_TEXTS.occasionInvalid }).regex(/^[A-Za-z0-9_.-]{1,100}$/, MAIL_TEXTS.occasionInvalid);

/**
 * Message-ID mit Datensatzbezug (Rückläufer finden so ihren Datensatz, Story 2.8):
 * `<entity_type.entity_id.mail_id@domain-des-absenders>` (reine Funktion).
 */
export function buildMessageId(input: { entityType: string; entityId: string; mailId: string; sender: string }): string {
  const domain = input.sender.includes('@') ? input.sender.split('@').pop()!.toLowerCase() : 'erp.invalid';
  const safeId = input.entityId.replace(/[^A-Za-z0-9_-]/g, '-');
  return `<${input.entityType}.${safeId}.${input.mailId}@${domain}>`;
}

// ---------------------------------------------------------------------------
// Rückläufer (Story 2.8, FR-44)

/** Lesende Operation: Unzustellbarkeitsmeldungen im Absender-Postfach abholen (Integration `graph-mail`). */
export const READ_BOUNCES_OPERATION = 'graph-mail.readBounces';

/** Woher ein Rückläufer kommt: Meldung im Postfach oder Ablehnung der Adresse beim Senden. */
export const bounceSources = ['ndr', 'send_rejected'] as const;
export type BounceSource = (typeof bounceSources)[number];

/** Grund eines Rückläufers in Kurzform (Ereignis, Datenbank); Text über `bounceReasonText`. */
export const bounceReasons = ['address_unknown', 'address_invalid', 'mailbox_full', 'too_large', 'rejected', 'unknown'] as const;
export type BounceReason = (typeof bounceReasons)[number];

const BOUNCE_REASON_TEXTS: Record<BounceReason, string> = {
  address_unknown: 'Adresse unbekannt',
  address_invalid: 'Adresse ungültig, von Microsoft 365 abgelehnt',
  mailbox_full: 'Postfach des Empfängers voll',
  too_large: 'E-Mail zu groß für den Empfänger',
  rejected: 'vom Empfänger abgelehnt',
  unknown: 'Grund nicht erkannt',
};

export function bounceReasonText(reason: BounceReason): string {
  return BOUNCE_REASON_TEXTS[reason];
}

/** Nutzlast der lesenden Operation im Ledger: nur das Zeitfenster (keine Personendaten, keine Geheimnisse). */
export const readBouncesPayload = z
  .object({ from: z.iso.datetime({ offset: true }), to: z.iso.datetime({ offset: true }) })
  .strict()
  .refine((p) => Date.parse(p.from) <= Date.parse(p.to), { message: 'Zeitfenster: Beginn nach Ende.' });
export type ReadBouncesPayload = z.infer<typeof readBouncesPayload>;

/** Ergebnis im Ledger: nur Zähler. */
export interface ReadBouncesResult {
  read: number;
  matched: number;
  unmatched: number;
  /** Danach geschlossene Klärfälle (nur live). */
  resolvedCases: number;
}

/** Klärfälle aus Rückläufern gehören `mail`; Ledger-Einträge des Lesens hängen am Datensatz „Absender-Postfach“. */
export const BOUNCE_CASE_OWNER = 'mail';
export const MAILBOX_ENTITY = { type: 'mailbox', id: 'sender' } as const;

export const BOUNCE_TEXTS = {
  caseTitle: (recipient: string) => `E-Mail an ${recipient} nicht zustellbar`,
  caseDetail: (subject: string, reasonText: string) =>
    `Die E-Mail „${subject}“ kam nicht an (${reasonText}). Bitte die Adresse prüfen und die E-Mail erneut senden; kommt sie an, schließt sich dieser Klärfall von selbst.`,
  caseResolution: 'Eine neuere E-Mail an diesem Datensatz wurde gesendet und kam nicht zurück.',
  scanForbidden: 'Nur Admins dürfen die Rückläufer von Hand abrufen.',
  settingsMissing: 'Die Einstellungen „Rückläufer prüfen alle“ und „Rückläufer rückwirkend lesen“ fehlen. Bitte unter Einstellungen › E-Mail eintragen.',
  /** Ablehnung der Empfängeradresse durch Microsoft 365 beim Senden (Graph 400); auch Ledger-Grund. */
  recipientRejected: 'Microsoft 365 hat die Empfängeradresse abgelehnt. Die E-Mail wird nicht erneut versucht.',
} as const;

// ---------------------------------------------------------------------------
// E-Mail aus dem Datensatz mit Vorlagen DE und EN (Story 2.7, FR-62, FR-73)

export const TEMPLATE_NAME_MAX = 100;
/** Höchstzahl Anhänge je E-Mail (technische Grenze; die Größe begrenzt die Einstellung `mail_attachment_max_mb`). */
export const MAIL_ATTACHMENTS_MAX = 20;

export const TEMPLATE_TEXTS = {
  nameEmpty: 'Bitte einen Namen für die Vorlage angeben.',
  nameTooLong: `Der Name ist zu lang (höchstens ${TEMPLATE_NAME_MAX} Zeichen).`,
  nameTaken: 'Eine Vorlage mit diesem Namen gibt es schon. Bitte einen anderen Namen wählen.',
  subjectDeEmpty: 'Bitte einen Betreff auf Deutsch angeben.',
  subjectEnEmpty: 'Bitte einen Betreff auf Englisch angeben.',
  bodyDeEmpty: 'Bitte einen Text auf Deutsch angeben.',
  bodyEnEmpty: 'Bitte einen Text auf Englisch angeben.',
  versionMissing: 'Bitte die gelesene Version der Vorlage mitschicken.',
  notFound: 'Diese Vorlage gibt es nicht.',
  versionNotFound: 'Diese Version der Vorlage gibt es nicht. Bitte die Vorlage neu wählen.',
  stale: 'Die Vorlage wurde inzwischen geändert. Die Seite zeigt jetzt den neuen Stand; bitte die Änderung noch einmal eintragen.',
  forbidden: 'Nur Admins dürfen E-Mail-Vorlagen ändern.',
  profileMissing: 'An diese Art von Datensatz kann man noch keine E-Mail schreiben.',
  attachmentForeign: 'Mindestens eine Datei gehört nicht zu diesem Datensatz. Bitte die Anhänge neu wählen.',
  attachmentDamaged: 'Eine Datei stimmt nicht mit der Ablage überein und wird nicht angehängt. Bitte Rudolf Bescheid geben.',
  attachmentsTooMany: `Bitte höchstens ${MAIL_ATTACHMENTS_MAX} Dateien anhängen.`,
} as const;

/**
 * „Beleg anhängen“ (Story 3.10, AD-8, FR-62): Sätze, warum eine Fassung nicht wählbar ist, und Fehler beim Senden.
 * Abnahme durch Steuerberater/Rudolf nötig.
 */
export const DOCUMENT_ATTACH_TEXTS = {
  rendering: 'wird noch erzeugt – erst danach anhängbar',
  invalid: 'ungültig – nicht anhängbar',
  purged: 'nach Fristende entfernt – nicht mehr anhängbar',
  fromProbe: 'aus der Probephase – nicht anhängbar',
  fromLive: 'aus dem Live-Betrieb – nicht anhängbar',
  missing: 'nicht mehr am Datensatz – nicht anhängbar',
  noArtifact: 'kein Archiv-Artefakt – nicht anhängbar',
  /** Neueste Fassung desselben Belegs wird noch erzeugt (Rudolf 06.10.2026: „Nur die neueste fertige Fassung“). */
  newerRendering: (versionNo: number) => `Fassung ${versionNo} wird noch erzeugt – erst danach anhängbar`,
  /** Neueste Fassung desselben Belegs ist ungültig. */
  newerInvalid: (versionNo: number) => `Fassung ${versionNo} ist ungültig – erst nach „Neu erzeugen“ anhängbar`,
  /** Neueste Fassung desselben Belegs ist versiegelt; nur sie darf hinaus. */
  superseded: (versionNo: number) => `ältere Fassung – anhängbar ist nur Fassung ${versionNo}`,
  /** Erneut senden und Prüfung vor dem Senden: mit dieser (älteren) Fassung geht die E-Mail nie mehr hinaus. */
  supersededNotSent: (versionNo: number) => `inzwischen gibt es Fassung ${versionNo} – bitte mit der neuesten Fassung neu senden`,
  staleChoice: 'Ein gewählter Beleg ist inzwischen nicht mehr anhängbar und wurde abgewählt. Bitte die Auswahl prüfen und erneut senden.',
  foreign: 'Mindestens ein Beleg gehört nicht zu diesem Datensatz. Bitte die Belege neu wählen.',
  damaged: 'Ein Beleg stimmt nicht mit dem Archiv überein und wird nicht angehängt. Bitte Rudolf Bescheid geben.',
  /** Story 11.25: Beleg ungültig abgelegt (z. B. ersetzter Lieferschein) – geht nie mehr hinaus. */
  voided: 'ungültig abgelegt – wird nicht angehängt',
} as const;

/** Was `documentAttachState` von einer Belegfassung braucht (Port `documents` der Datensatz-Art in `collab`). */
export interface AttachableDocumentVersion {
  /** Beleg, zu dem die Fassung gehört (Fassungen desselben Belegs haben dieselbe `documentId`). */
  documentId: string;
  versionNo: number;
  status: 'rendering' | 'sealed' | 'invalid';
  purged: boolean;
  runMode: 'live' | 'probe';
  /** Story 11.25: Beleg ungültig abgelegt (Vermerk in `documents.document_void`). */
  voided: boolean;
}

/** Darf diese Fassung an eine E-Mail? Sonst mit Grund (Satz aus `DOCUMENT_ATTACH_TEXTS`). */
export type DocumentAttachState = { attachable: true; reason: null } | { attachable: false; reason: string };

/**
 * Darf die Belegfassung als Anhang hinaus (reine Funktion, Story 3.10, AD-8, Fund B6)? Nur die neueste Fassung ihres
 * Belegs, nur versiegelt, nicht nach Fristende entfernt und aus demselben Laufmodus wie die E-Mail (Rudolf 06.10.2026:
 * „Nur die neueste fertige Fassung“). `versions` = Fassungen des Datensatzes, wie der Port `documents` sie liefert;
 * „neueste“ = höchste `versionNo` derselben `documentId` darin. Reihenfolge der Gründe: entfernt, ungültig abgelegt, anderer Laufmodus,
 * neuere Fassung vorhanden, eigener Status. Gleiche Regel wie `sendStateOf` (documents): versendbar nur die jüngste
 * versiegelte Fassung.
 */
export function documentAttachState(
  version: AttachableDocumentVersion,
  runMode: 'live' | 'probe',
  versions: readonly AttachableDocumentVersion[],
): DocumentAttachState {
  if (version.purged) return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.purged };
  if (version.voided) return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.voided };
  if (version.runMode !== runMode) return { attachable: false, reason: version.runMode === 'probe' ? DOCUMENT_ATTACH_TEXTS.fromProbe : DOCUMENT_ATTACH_TEXTS.fromLive };
  const newest = newestVersionOf(version, versions);
  if (newest !== version) {
    if (newest.status === 'rendering') return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.newerRendering(newest.versionNo) };
    if (newest.status === 'invalid') return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.newerInvalid(newest.versionNo) };
    return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.superseded(newest.versionNo) };
  }
  if (version.status === 'rendering') return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.rendering };
  if (version.status === 'invalid') return { attachable: false, reason: DOCUMENT_ATTACH_TEXTS.invalid };
  return { attachable: true, reason: null };
}

/** Fassung mit der höchsten `versionNo` desselben Belegs in `versions` (mindestens `version` selbst). */
function newestVersionOf(version: AttachableDocumentVersion, versions: readonly AttachableDocumentVersion[]): AttachableDocumentVersion {
  let newest = version;
  for (const v of versions) if (v.documentId === version.documentId && v.versionNo > newest.versionNo) newest = v;
  return newest;
}

/** Nummer der neuesten Fassung desselben Belegs, wenn es eine neuere als `version` gibt; sonst `null`. */
export function newerVersionNo(version: AttachableDocumentVersion, versions: readonly AttachableDocumentVersion[]): number | null {
  const newest = newestVersionOf(version, versions);
  return newest === version ? null : newest.versionNo;
}

/**
 * Satz für den endgültigen Fehlschlag, wenn ein Beleg-Anhang direkt vor dem Senden nicht mehr hinaus darf (Story 3.10),
 * z. B. „Nicht gesendet: Beleg MUSTER-0001 Fassung 2 wurde nach Fristende entfernt.“ `why` = `purged`, `damaged` oder
 * ein Satz aus `DOCUMENT_ATTACH_TEXTS`/`documentAttachState`.
 */
export function documentNotSentText(number: string, versionNo: number, why: 'purged' | 'damaged' | string): string {
  const head = `Nicht gesendet: Beleg ${number} Fassung ${versionNo}`;
  if (why === 'purged') return `${head} wurde nach Fristende entfernt.`;
  if (why === 'damaged') return `${head} stimmt nicht mehr mit dem Archiv überein.`;
  return `${head} – ${why}.`;
}

/** Fassung mit Nummer und Artefakten, wie der Port `documents` in `collab` sie liefert. */
export interface ResolvableDocumentVersion<A extends { id: string }> extends AttachableDocumentVersion {
  number: string;
  artifacts: readonly A[];
}

/** Ergebnis von `resolveDocumentAttachment`: Fassung und Artefakt oder der Satz für 422 `errors.documents`. */
export type ResolvedDocumentAttachment<V, A> = { ok: true; version: V; artifact: A } | { ok: false; error: string };

/**
 * Gewähltes Artefakt unter den Belegen des Datensatzes finden und prüfen (reine Funktion, Story 3.10; Server-Vorprüfung
 * und Command): fremd → `foreign`; nicht anhängbar (auch: neuere Fassung desselben Belegs vorhanden) → „<Nummer>
 * Fassung <n>: <Satz aus D3>“.
 */
export function resolveDocumentAttachment<A extends { id: string }, V extends ResolvableDocumentVersion<A>>(
  versions: readonly V[],
  artifactId: string,
  runMode: 'live' | 'probe',
): ResolvedDocumentAttachment<V, A> {
  for (const version of versions) {
    const artifact = version.artifacts.find((a) => a.id === artifactId);
    if (!artifact) continue;
    const state = documentAttachState(version, runMode, versions);
    if (!state.attachable) return { ok: false, error: `${version.number} Fassung ${version.versionNo}: ${state.reason}` };
    return { ok: true, version, artifact };
  }
  return { ok: false, error: DOCUMENT_ATTACH_TEXTS.foreign };
}

/** Dateiname des Beleg-Anhangs wie „PDF öffnen“, z. B. `MUSTER-0001-Fassung-2.pdf` (nur sichere Zeichen). */
export function documentAttachmentFileName(number: string, versionNo: number, kind: string): string {
  const extension = kind.replace(/[^A-Za-z0-9]/g, '') || 'bin';
  return `${number}-Fassung-${versionNo}.${extension}`.replace(/[^A-Za-z0-9._-]/g, '_');
}

/** „MUSTER-0001 · Fassung 2“ (Auswahl im E-Mail-Fenster). */
export function documentChoiceText(number: string, versionNo: number): string {
  return `${number} · Fassung ${versionNo}`;
}

/** „Anhang MUSTER-0001 Fassung 2“ (Chatter, Story 3.10). */
export function documentAttachmentText(document: { number: string; versionNo: number }): string {
  return `Anhang ${document.number} Fassung ${document.versionNo}`;
}

/** „Die Anhänge sind zusammen zu groß (höchstens 2 MB).“ – Grenze aus der Einstellung `mail_attachment_max_mb`. */
export function attachmentsTooLargeText(maxBytes: number): string {
  return `Die Anhänge sind zusammen zu groß (höchstens ${formatMegabytes(maxBytes)} MB).`;
}

/** „Platzhalter ‚Kundennummer‘ ist leer – bitte ergänzen oder aus dem Text entfernen.“ */
export function placeholderMissingText(names: readonly string[]): string {
  const list = names.map((n) => `‚${n}‘`).join(', ');
  return names.length === 1
    ? `Platzhalter ${list} ist leer – bitte ergänzen oder aus dem Text entfernen.`
    : `Platzhalter ${list} sind leer – bitte ergänzen oder aus dem Text entfernen.`;
}

/** „Den Platzhalter ‚Kundenummer‘ gibt es nicht. Möglich sind: {{Name}}, …“ (Vorlage speichern, Senden von Hand). */
export function placeholderUnknownText(names: readonly string[], allowed: readonly string[]): string {
  const list = names.map((n) => `‚${n}‘`).join(', ');
  const possible = allowed.map((a) => `{{${a}}}`).join(', ');
  return `${names.length === 1 ? 'Den Platzhalter' : 'Die Platzhalter'} ${list} gibt es nicht. Möglich sind: ${possible}.`;
}

/**
 * Satz zu unbekannten und leeren Platzhaltern (Vorschau und Senden von Hand); `null`, wenn es keine gibt. Unbekannt ist
 * nur, was im ganzen Katalog fehlt; ein Name ohne Wert an dieser Datensatz-Art ist leer (Satz aus Story 2.7).
 */
export function missingPlaceholdersText(empty: readonly string[], unknown: readonly string[], allowed: readonly string[]): string | null {
  const parts = [...(unknown.length > 0 ? [placeholderUnknownText(unknown, allowed)] : []), ...(empty.length > 0 ? [placeholderMissingText(empty)] : [])];
  return parts.length > 0 ? parts.join(' ') : null;
}

const templateName = z.string({ error: TEMPLATE_TEXTS.nameEmpty }).trim().min(1, TEMPLATE_TEXTS.nameEmpty).max(TEMPLATE_NAME_MAX, TEMPLATE_TEXTS.nameTooLong);
const templateSubject = (empty: string) =>
  z.string({ error: empty }).trim().min(1, empty).max(MAIL_SUBJECT_MAX, MAIL_TEXTS.subjectTooLong);
const templateBody = (empty: string) => z.string({ error: empty }).refine((v) => v.trim().length > 0, empty).pipe(z.string().max(MAIL_BODY_MAX, MAIL_TEXTS.bodyTooLong));

/** Inhalt einer Vorlage (Formular und Schnittstelle mit denselben Regeln). */
export const templateContentSchema = z.object({
  name: templateName,
  subjectDe: templateSubject(TEMPLATE_TEXTS.subjectDeEmpty),
  bodyDe: templateBody(TEMPLATE_TEXTS.bodyDeEmpty),
  subjectEn: templateSubject(TEMPLATE_TEXTS.subjectEnEmpty),
  bodyEn: templateBody(TEMPLATE_TEXTS.bodyEnEmpty),
});
export type TemplateContent = z.infer<typeof templateContentSchema>;

/** Felder der Vorlage, die Platzhalter enthalten dürfen. */
export const templateTextFields = ['subjectDe', 'bodyDe', 'subjectEn', 'bodyEn'] as const;

/** Betreff und Text einer Vorlage in einer Sprache. */
export function templateTextIn(t: Pick<TemplateContent, 'subjectDe' | 'bodyDe' | 'subjectEn' | 'bodyEn'>, language: Language): { subject: string; body: string } {
  return language === 'en' ? { subject: t.subjectEn, body: t.bodyEn } : { subject: t.subjectDe, body: t.bodyDe };
}

// ---------------------------------------------------------------------------
// E-Mail-Signatur je Benutzer (Story 2.14, FR-73)

/**
 * Technische Obergrenze der Signatur (Schutz für Datenbank und Formular, auch Höchstwert der Einstellung). Die
 * fachliche Höchstlänge ist die versionierte Einstellung `mail_signature_max_chars` (Startwert 1000).
 */
export const SIGNATURE_TECH_MAX = 10_000;

export const SIGNATURE_TEXTS = {
  tooLong: (max: number) => `Die Signatur ist zu lang (höchstens ${max} Zeichen).`,
  versionMissing: 'Bitte die gelesene Version der Signatur mitschicken.',
  stale: 'Die Signatur wurde inzwischen an anderer Stelle geändert. Die Seite zeigt jetzt den neuen Stand; bitte die Änderung noch einmal eintragen.',
  forbidden: 'Die Signatur kann nur die angemeldete Person selbst ändern.',
} as const;

/** Hinweis zu den Bausteinen (Einstellungen › E-Mail-Vorlagen). */
export const TEMPLATE_BUILDING_BLOCKS_HINT =
  'Bausteine: {{Heute | lang}} schreibt das Datum aus („3. Februar 2027“). {{#wenn Name}} … {{#sonst}} … {{/wenn}} zeigt Text nur, wenn der Platzhalter einen Wert hat (0 zählt als „kein Wert“); {{#wenn nicht Name}} umgekehrt; {{#wenn Zahlart = Vorkasse}} vergleicht mit einem Wert aus einer festen Liste. Erlaubtes HTML: <p>, <br>, <strong>, <em>, <ul>, <ol>, <li> und <a href="https://…">.';

// ---------------------------------------------------------------------------
// Vorlagen je Anlass und automatischer Versand (Story 2.16, AD-22, AD-34)

/**
 * Anlässe der E-Mail-Vorlagen (Kennungen nie umbenennen). `manual` = frei (Vorlagen für das Senden von Hand und
 * Prüfungen, jede Art von Datensatz); die übrigen gelten nur für die Datensatz-Arten, deren Profil sie anmeldet.
 */
export const mailOccasions = [
  'order_confirmation',
  'proforma',
  'invoice',
  'shipping_info',
  'quote',
  'dunning_reminder',
  'dunning_1',
  'dunning_2',
  'prepayment_reminder',
  'return_label',
  /** Werbung (Story 2.29), z. B. Nachkauf-Hinweise: mit Abmeldesatz, Abmelde-Link und Werbe-Sperre. */
  'advertising',
  'manual',
] as const;
export type MailOccasion = (typeof mailOccasions)[number];

/** Anlass, den bestehende und neue Vorlagen ohne Angabe bekommen. */
export const MANUAL_OCCASION: MailOccasion = 'manual';

const MAIL_OCCASION_LABELS: Record<MailOccasion, string> = {
  order_confirmation: 'Auftragsbestätigung',
  proforma: 'Proforma-Rechnung',
  invoice: 'Rechnung',
  shipping_info: 'Versandinfo',
  quote: 'Angebot',
  dunning_reminder: 'Zahlungserinnerung',
  dunning_1: '1. Mahnung',
  dunning_2: '2. Mahnung',
  prepayment_reminder: 'Vorkasse-Erinnerung',
  return_label: 'Retourenschein',
  advertising: 'Werbung',
  manual: 'Frei (von Hand)',
};

export function mailOccasionLabel(occasion: MailOccasion): string {
  return MAIL_OCCASION_LABELS[occasion];
}

export function isMailOccasion(code: string): code is MailOccasion {
  return (mailOccasions as readonly string[]).includes(code);
}

export const OCCASION_TEXTS = {
  unknown: 'Diesen Anlass kennt das ERP nicht.',
  unknownChoose: 'Den Anlass dieser Vorlage kennt das ERP nicht mehr. Bitte einen Anlass wählen.',
  templateMismatch: 'Die Vorlage gehört zu einem anderen Anlass.',
  entityNotAllowed: 'Für diesen Anlass gibt es an dieser Art von Datensatz keine E-Mail.',
  testSendForbidden: 'Nur Admins dürfen einen Prüfversand auslösen.',
  testSendOnlyOnTest: 'Den Prüfversand gibt es nur auf der Testumgebung.',
  /** Klärfall (Besitzer `mail`): eine Lücke. */
  caseTitleOne: (template: string, record: string, problem: string) => `E-Mail ‚${template}‘ an ${record} nicht gesendet: ${problem}`,
  /** Klärfall: mehrere Lücken am Datensatz. */
  caseTitleMany: (count: number, record: string) => `${count} E-Mails an ${record} nicht gesendet`,
  caseLine: (occasion: string, template: string, problem: string) => `${occasion} (Vorlage ‚${template}‘): ${problem}.`,
  caseHint: 'Bitte die Angaben am Datensatz ergänzen oder die Vorlage anpassen. Gelingt der Versand, schließt sich dieser Klärfall von selbst.',
  caseResolution: 'Alle zurückgehaltenen E-Mails an diesem Datensatz sind gesendet.',
  /** Klärfall (Story 2.39): gerenderter Betreff über der Grenze. */
  caseSubjectTitle: (template: string, record: string) => `E-Mail ‚${template}‘ an ${record} nicht gesendet: Betreff zu lang`,
  caseSubjectDetail: `Der Betreff der Vorlage ist nach dem Einsetzen der Platzhalter länger als ${MAIL_SUBJECT_MAX} Zeichen. Bitte die Vorlage kürzen oder die Angaben am Datensatz prüfen. Gelingt später eine E-Mail an diesen Datensatz, schließt sich dieser Klärfall von selbst.`,
  caseSubjectResolution: 'Eine E-Mail an diesem Datensatz wurde eingestellt.',
} as const;

/** „Platzhalter ‚IBAN‘ leer“, „Platzhalter ‚A‘, ‚B‘ leer, ‚X‘ unbekannt“ (Klärfall, kurze Form). */
export function placeholderProblemText(empty: readonly string[], unknown: readonly string[]): string {
  const list = (names: readonly string[]) => names.map((n) => `‚${n}‘`).join(', ');
  const parts = [...(empty.length > 0 ? [`${list(empty)} leer`] : []), ...(unknown.length > 0 ? [`${list(unknown)} unbekannt`] : [])];
  return `Platzhalter ${parts.join(', ')}`;
}

/** Anlass einer Vorlage (Formular und Schnittstelle). */
export const templateOccasionSchema = z.enum(mailOccasions, { error: OCCASION_TEXTS.unknown });

// ---------------------------------------------------------------------------
// Vorlagen-Editor (Story 2.15, EXPERIENCE §4.11, AD-34 Editor-API)

/**
 * Gruppe des Platzhalter-Katalogs, die im Editor zuerst steht, je Anlass (`manual` = keine; Katalog-Reihenfolge bleibt).
 */
const OCCASION_PLACEHOLDER_GROUPS: Record<MailOccasion, MailPlaceholderGroup | null> = {
  order_confirmation: 'auftrag',
  proforma: 'rechnung',
  invoice: 'rechnung',
  shipping_info: 'versand',
  quote: 'angebot',
  dunning_reminder: 'mahnung',
  dunning_1: 'mahnung',
  dunning_2: 'mahnung',
  prepayment_reminder: 'zahlung',
  return_label: 'retoure',
  advertising: null,
  manual: null,
};

export function occasionPlaceholderGroup(occasion: MailOccasion): MailPlaceholderGroup | null {
  return OCCASION_PLACEHOLDER_GROUPS[occasion];
}

/** Kennung des Ersatz-Beispielfalls, wenn kein Besitzer einen Fall für den Anlass anmeldet. */
export const EXAMPLE_VALUES_ID = 'beispielwerte';

export const EDITOR_TEXTS = {
  exampleValuesLabel: 'Beispielwerte',
  exampleUnknown: 'Diesen Beispielfall gibt es für diesen Anlass nicht. Bitte einen Beispielfall wählen.',
  exampleOrRecord: 'Bitte entweder einen Beispielfall oder einen Datensatz wählen, nicht beides.',
  allFilled: 'Alle Platzhalter gefüllt.',
  emptyInExample: (names: readonly string[]) => `Bei diesem Beispiel leer: ${names.join(', ')}`,
  emptyInRecord: (names: readonly string[]) => `Bei diesem Datensatz leer: ${names.join(', ')}`,
  missingIn: (language: 'DE' | 'EN', name: string) => `In ${language} fehlt {{${name}}}`,
  noTable: 'Für diesen Anlass gibt es keine Positionstabelle.',
  stale: 'Diese Vorlage hat inzwischen jemand geändert – bitte neu laden.',
  previewFailed: 'Die Vorlage konnte vor dem Speichern nicht geprüft werden; es wurde nichts gespeichert.',
  emptyBlocksSending:
    'Ist ein Platzhalter beim Senden leer, geht die E-Mail nicht hinaus: von Hand erscheint ein Hinweis, beim automatischen Versand ein Klärfall.',
} as const;

/**
 * „Platzhalter ‚Rechnungsnumer‘ gibt es nicht – bitte über „Platzhalter“ einfügen.“ (Editor, rot am Feld; EXPERIENCE
 * §4.11). Beim Speichern bleibt der Satz mit der Liste der möglichen Platzhalter (`placeholderUnknownText`).
 */
export function placeholderUnknownEditorText(names: readonly string[]): string {
  return `Platzhalter ${names.map((n) => `‚${n}‘`).join(', ')} gibt es nicht – bitte über „Platzhalter“ einfügen.`;
}

const previewText = (max: number, tooLong: string) => z.string().max(max, tooLong);

/**
 * Eingabe der Live-Vorschau im Editor (`POST /mail/templates/preview`): Texte wie `templateContentSchema` (Längen),
 * leere Felder erlaubt; Beispielfall (`example`) oder echter Datensatz (`record`), ohne beides der erste Beispielfall.
 */
export const templatePreviewInputSchema = z
  .object({
    occasion: templateOccasionSchema,
    language: z.enum(['de', 'en']),
    subjectDe: previewText(MAIL_SUBJECT_MAX, MAIL_TEXTS.subjectTooLong),
    bodyDe: previewText(MAIL_BODY_MAX, MAIL_TEXTS.bodyTooLong),
    subjectEn: previewText(MAIL_SUBJECT_MAX, MAIL_TEXTS.subjectTooLong),
    bodyEn: previewText(MAIL_BODY_MAX, MAIL_TEXTS.bodyTooLong),
    example: z.string().min(1).max(63).optional(),
    record: z
      .object({ entityType: z.string().regex(/^[a-z][a-z0-9_]*$/, MAIL_TEXTS.entityUnknown).max(60), entityId: z.string().min(1).max(200) })
      .strict()
      .optional(),
  })
  .strict()
  .refine((v) => v.example === undefined || v.record === undefined, { message: EDITOR_TEXTS.exampleOrRecord, path: ['record'] });
export type TemplatePreviewInput = z.infer<typeof templatePreviewInputSchema>;

// ---------------------------------------------------------------------------
// Werbe-Abmeldung per Klick (Story 2.29, FR-116, FR-62, AD-20, AD-22)

/** Anlass „Werbung“: E-Mails aus Vorlagen dieses Anlasses sind Werbe-Mails. */
export const ADVERTISING_OCCASION: MailOccasion = 'advertising';

/** Pfad der öffentlichen Abmeldeseite; dahinter steht nur der zufällige Verweis. */
export const UNSUBSCRIBE_PATH_PREFIX = '/abmelden/';

/** Verweis im Link: 32 zufällige Byte als base64url (43 Zeichen, 256 Bit). */
export const UNSUBSCRIBE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export const ADVERTISING_TEXTS = {
  /** Satz unter jeder Werbe-Mail (wörtlich aus der Story; Abnahme durch Rudolf nötig). */
  footer: 'Sie möchten solche Hinweise nicht mehr erhalten? Hier abmelden.',
  /** Englischer Satz für Kunden mit anderer Belegsprache als Deutsch (Rudolf 07.10.2026). */
  footerEn: "Don't want to receive these notes anymore? Unsubscribe here.",
  /** Senden an eine gesperrte Adresse (enqueue, von Hand, Vorlage). */
  unsubscribed: 'Kunde hat Werbe-Mails abgemeldet',
  /** Endgültiger Fehlschlag im Sende-Step: nach dem Einreihen abgemeldet. */
  notSent: 'Nicht gesendet: Der Kunde hat Werbe-Mails an diese Adresse abgemeldet.',
  noCustomer: 'Werbe-Mails gehen nur an Kunden. An diese Art von Datensatz gibt es keine Werbe-Mail.',
  noOrigin: 'Die Adresse des ERP ist nicht eingerichtet (ERP_DOMAIN). Werbe-Mails brauchen sie für den Abmelde-Link.',
  noReader: 'Die Werbe-Sperrliste ist nicht angemeldet; Werbe-Mails gehen darum nicht hinaus.',
  linkMissing: 'Nicht gesendet: Der Werbe-Mail fehlt der Abmelde-Link.',
  addressNotAccepted: 'An diese Adresse kann keine Werbe-Mail gehen, weil die Abmeldung für sie nicht möglich wäre. Bitte eine gültige E-Mail-Adresse angeben.',
} as const;

/** Sätze der öffentlichen Abmeldeseite (wörtlich aus der Story; Abnahme durch Rudolf nötig). */
export const UNSUBSCRIBE_PAGE_TEXTS = {
  title: 'Abmelden',
  question: 'Keine Hinweise mehr per E-Mail?',
  questionEn: 'No more notes by email?',
  button: 'Abmelden / Unsubscribe',
  done: 'Sie sind abgemeldet.',
  doneEn: 'You have been unsubscribed.',
  invalid: 'Dieser Link ist ungültig.',
  invalidEn: 'This link is invalid.',
  tooMany: 'Zu viele Anfragen. Bitte in einer Minute noch einmal versuchen.',
  tooManyEn: 'Too many requests. Please try again in a minute.',
  error: 'Das hat gerade nicht geklappt. Bitte später noch einmal versuchen.',
  errorEn: 'That did not work. Please try again later.',
} as const;

/** Abmelde-Link aus Adresse des ERP (`https://…`, ohne Schrägstrich am Ende) und Verweis (reine Funktion). */
export function unsubscribeUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}${UNSUBSCRIBE_PATH_PREFIX}${token}`;
}

/** Text der Werbe-Mail mit Abmeldesatz und Link am Ende (reine Funktion). */
/** Sprache des Abmeldesatzes: Belegsprache Deutsch (oder unbekannt) → Deutsch, jede andere → Englisch (Rudolf 07.10.2026). */
export function advertisingFooterLanguage(documentLanguage: string | null): 'de' | 'en' {
  return documentLanguage === null || documentLanguage === 'de' ? 'de' : 'en';
}

export function withAdvertisingFooter(body: string, url: string, language: 'de' | 'en' = 'de'): string {
  return `${body.replace(/\s+$/, '')}\n\n${language === 'en' ? ADVERTISING_TEXTS.footerEn : ADVERTISING_TEXTS.footer}\n${url}`;
}

/**
 * Abmelde-Link am Ende einer Werbe-Mail (`withAdvertisingFooter`); `null`, wenn der Text nicht so endet (reine Funktion).
 * Für die Kopfzeilen List-Unsubscribe beim Senden und für „Erneut senden“ (gleicher Link, gleicher Verweis).
 */
export function unsubscribeUrlOf(body: string): string | null {
  const lines = body.split('\n');
  const url = lines[lines.length - 1] ?? '';
  const sentence = lines[lines.length - 2];
  if (sentence !== ADVERTISING_TEXTS.footer && sentence !== ADVERTISING_TEXTS.footerEn) return null;
  const match = /^https?:\/\/[^\s/]+(\/abmelden\/)([A-Za-z0-9_-]{43})$/.exec(url);
  return match ? url : null;
}
